import { countryCodes } from '../../pages/analytics/Data/dropdown';

// AD-310: how "Filter by Country" works on each platform. The backend holds the same table
// (utils/countryFilter.js) and is what actually applies it; this copy decides what the
// control offers before a request is made.
//
//   api     the country goes to SoundCharts. Apple Music and Amazon tag every playlist.
//   local   the backend filters the complete set, so playlists with no country code are
//           counted and can be picked as a bucket of their own.
//   hidden  the platform carries no country data, so the control is not shown.
export type CountryFilterMode = 'api' | 'local' | 'hidden';

const COUNTRY_FILTER_BY_PLATFORM: Record<string, CountryFilterMode> = {
  'apple-music': 'api',
  amazon: 'api',
  spotify: 'local',
  audiomack: 'local',
  // Hidden or relabelled is Sam's call; until then it behaves as before.
  deezer: 'api',
  tidal: 'hidden',
  youtube: 'hidden',
  soundcloud: 'hidden',
  // The pitch analytics page's key for SoundCloud.
  'sound-cloud': 'hidden',
};

/** The value sent to ask for the playlists with no country code. */
export const NO_COUNTRY = 'none';

export const countryFilterMode = (platform: string): CountryFilterMode =>
  COUNTRY_FILTER_BY_PLATFORM[platform] || 'api';

export interface CountryOption {
  value: string;
  label: string;
}

/**
 * The countries the control offers on this mode. "No country code" is offered only where the
 * backend filters locally: SoundCharts cannot be asked for playlists without a country.
 */
export const countryOptionsFor = (mode: CountryFilterMode): CountryOption[] =>
  Object.entries(countryCodes).flatMap(([code, name]) => {
    if (code !== '') return [{ value: code, label: name }];
    return mode === 'local' ? [{ value: NO_COUNTRY, label: name }] : [];
  });

/** Whether a picked country still makes sense on this mode, after a platform change. */
export const countryAllowed = (value: string | undefined, mode: CountryFilterMode): boolean =>
  !value || (mode !== 'hidden' && (value !== NO_COUNTRY || mode === 'local'));
